import { Box } from '@chakra-ui/react';
import type { RegistrationProgressResponse } from '@diamond/shared/features/registration/browser';
import { formatRegistrationSubmittedAt } from '@diamond/shared/features/registration/browser';
import { usePageViewTracking } from '@diamond/shared/utils';
import { TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { SEONext } from '@diamond/sol-buyer/seo-next';
import { useNooRegistrationStatus } from '@diamond/sol-buyer/utils';
import { useRouter } from 'next/router';
import { type ReactElement } from 'react';

import { BuyerProfileLayout } from '../profile/BuyerProfileLayout';
import { DesktopStatusContent } from './NooRegistrationStatusDesktop';
import { NooRegistrationStatusMobile } from './NooRegistrationStatusMobile';
import {
  DesktopAccountTabs,
  NooStatusPageHeader,
} from './NooRegistrationStatusSharedUi';

function NooRegistrationStatusErrorState({
  error,
  registrationToken,
  onRetry,
}: {
  error: string;
  registrationToken: string;
  onRetry: () => void;
}): ReactElement {
  return (
    <Box
      border="1px solid"
      borderColor="#FECACA"
      bg="#FEF2F2"
      borderRadius="xl"
      p={4}
      data-testid="noo-buyer-status-error"
    >
      <Box color="#DC2626" fontSize="sm">
        {error}
      </Box>
      {registrationToken ? (
        <Box
          as="button"
          type="button"
          mt={4}
          px={3}
          py={2}
          fontSize="sm"
          border="1px solid"
          borderColor="#D1D5DB"
          borderRadius="md"
          bg="#FFFFFF"
          onClick={() => void onRetry()}
        >
          Coba Lagi
        </Box>
      ) : null}
    </Box>
  ) as ReactElement;
}

function NooRegistrationStatusMainContent({
  progress,
  displayInfo,
  verificationSections,
  registrationToken,
  submittedAtLabel,
  successInfo,
  showSuccessBanner,
  isStatusVerificationView,
  initialSectionId,
}: {
  progress: RegistrationProgressResponse;
  displayInfo: ReturnType<typeof useNooRegistrationStatus>['displayInfo'];
  verificationSections: ReturnType<
    typeof useNooRegistrationStatus
  >['verificationSections'];
  registrationToken: string;
  submittedAtLabel: string | null;
  successInfo: ReturnType<typeof useNooRegistrationStatus>['successInfo'];
  showSuccessBanner: boolean;
  isStatusVerificationView: boolean;
  initialSectionId: string | null;
}): ReactElement {
  return (
    <Box data-testid="noo-buyer-registration-status">
      <Box display={{ base: 'none', lg: 'block' }}>
        <BuyerProfileLayout navigation={<DesktopAccountTabs disablePasswordTab />}>
          <Box mt={6}>
            <DesktopStatusContent
              progress={progress}
              displayInfo={displayInfo}
              verificationSections={verificationSections}
              registrationToken={registrationToken}
              submittedAtLabel={submittedAtLabel}
              successInfo={successInfo}
              showSuccessBanner={showSuccessBanner}
              isStatusVerificationView={isStatusVerificationView}
              initialSectionId={initialSectionId}
            />
          </Box>
        </BuyerProfileLayout>
      </Box>

      <NooRegistrationStatusMobile
        isStatusVerificationView={isStatusVerificationView}
        displayInfo={displayInfo}
        submittedAtLabel={submittedAtLabel}
        showSuccessBanner={showSuccessBanner}
        successInfo={successInfo}
        verificationSections={verificationSections}
        registrationToken={registrationToken}
      />
    </Box>
  ) as ReactElement;
}

export function NooRegistrationStatusPage(): ReactElement {
  usePageViewTracking({ pageName: TRACKING_PAGES.NOO_REGISTRATION_STATUS });
  const router = useRouter();
  const sectionQuery = router.query['section'];
  const initialSectionId =
    typeof sectionQuery === 'string' ? sectionQuery : null;

  const {
    progress,
    isLoading,
    error,
    registrationToken,
    loadStatus,
    displayInfo,
    verificationSections,
    successInfo,
    showSuccessBanner,
    isStatusVerificationView,
  } = useNooRegistrationStatus();

  const submittedAtLabel = formatRegistrationSubmittedAt(
    progress?.registration_submitted_at ?? null
  );

  let body: ReactElement | null = null;

  if (isLoading) {
    body = (
      <Box
        display="flex"
        justifyContent="center"
        py={12}
        data-testid="noo-buyer-status-loading"
      >
        <Box
          w="40px"
          h="40px"
          border="4px solid #E5E7EB"
          borderTopColor="#2563EB"
          borderRadius="full"
          sx={{
            '@keyframes nooStatusSpin': {
              to: { transform: 'rotate(360deg)' },
            },
            animation: 'nooStatusSpin 1s linear infinite',
          }}
          aria-label="Memuat"
        />
      </Box>
    ) as ReactElement;
  } else if (error) {
    body = (
      <NooRegistrationStatusErrorState
        error={error}
        registrationToken={registrationToken}
        onRetry={loadStatus}
      />
    ) as ReactElement;
  } else if (progress) {
    body = (
      <NooRegistrationStatusMainContent
        progress={progress}
        displayInfo={displayInfo}
        verificationSections={verificationSections}
        registrationToken={registrationToken}
        submittedAtLabel={submittedAtLabel}
        successInfo={successInfo}
        showSuccessBanner={showSuccessBanner}
        isStatusVerificationView={isStatusVerificationView}
        initialSectionId={initialSectionId}
      />
    ) as ReactElement;
  }

  return (
    <>
      <SEONext title={TRACKING_PAGES.NOO_REGISTRATION_STATUS} />
      <Box minH="100vh" bg={{ base: '#FFFFFF', lg: '#FCFCFB' }}>
        <NooStatusPageHeader />
        <Box
          maxW={{ base: '390px', lg: '1296px' }}
          w="full"
          mx="auto"
          px={{ base: 4, lg: 0 }}
          pt={{ base: 6, lg: 8 }}
          pb={{ base: 8, lg: 10 }}
        >
          {body}
        </Box>
      </Box>
    </>
  ) as ReactElement;
}
